import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Tabs as TabsPrimitive } from '@base-ui/react/tabs'

import { cn } from '@/registry/reapit/lib/utils'

// ---------------------------------------------------------------------------
// Tabs — migrated from Radix to Base UI
// ---------------------------------------------------------------------------
// API mapping:
//   Radix Tabs.Root       → Base UI Tabs.Root
//   Radix Tabs.List       → Base UI Tabs.List
//   Radix Tabs.Trigger    → Base UI Tabs.Tab
//   Radix Tabs.Content    → Base UI Tabs.Panel
//
// Data attribute changes:
//   data-[state=active]   → data-[active]
//   data-[state=inactive] → absence of data-[active]
//
// Prop changes:
//   Radix uses `defaultValue` / `value` / `onValueChange`
//   Base UI uses the same prop names but with `TabsTab.Value` type
// ---------------------------------------------------------------------------

function Tabs({ className, orientation = 'horizontal', ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      orientation={orientation}
      className={cn('group/tabs flex gap-2 data-[orientation=horizontal]:flex-col', className)}
      {...props}
    />
  )
}

const tabsListVariants = cva(
  // Base: DS S/Medium 14px text, muted colour for inactive items
  'group/tabs-list inline-flex w-fit items-center justify-center text-sm font-medium text-muted-foreground group-data-[orientation=horizontal]/tabs:h-10 group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col',
  {
    variants: {
      variant: {
        /**
         * DS Primary tabs — page-level underline style.
         * Tab list sits on a bottom border; active tab renders a primary-coloured underline indicator.
         */
        default: 'gap-1 rounded-none bg-transparent border-b border-border px-0',
        /**
         * DS Folder tabs — filled container per tab, lighter and scoped to cards/panels.
         * Each tab gets a rounded pill background; active tab uses background + foreground colours.
         */
        folder: 'gap-1 rounded-lg bg-muted p-1',
        /**
         * DS PrimaryTabs alias (explicit name for page-level tabs).
         * Identical to `default`; provided for clarity in consuming code.
         */
        primary: 'gap-1 rounded-none bg-transparent border-b border-border px-0',
        /** DS SecondaryTabs — divider-separated items */
        secondary: 'gap-0 rounded-none bg-transparent divide-x divide-border px-0',
        /** @deprecated use `default` or `primary` */
        line: 'gap-1 rounded-none bg-transparent border-b border-border px-0',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

function TabsList({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// TabsTrigger (wraps Base UI Tabs.Tab)
// ---------------------------------------------------------------------------
// We keep the export name as TabsTrigger to preserve the public API.
// Base UI uses data-[active] instead of Radix's data-[state=active].
// ---------------------------------------------------------------------------

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Tab>) {
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(
        // Base trigger — DS S Medium 14px, icon-md (size-5) per Principal policy for nav
        // Base UI: data-[active] replaces Radix data-[state=active]
        "relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-2 border border-transparent px-3 py-1 text-sm font-medium whitespace-nowrap text-muted-foreground transition-all group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
        // default / primary (underline) variant — active tab uses primary colour underline
        'group-data-[variant=default]/tabs-list:rounded-none group-data-[variant=default]/tabs-list:bg-transparent group-data-[variant=default]/tabs-list:pb-3 group-data-[variant=default]/tabs-list:data-[active]:bg-transparent group-data-[variant=default]/tabs-list:data-[active]:text-foreground',
        'group-data-[variant=primary]/tabs-list:rounded-none group-data-[variant=primary]/tabs-list:bg-transparent group-data-[variant=primary]/tabs-list:pb-3 group-data-[variant=primary]/tabs-list:data-[active]:bg-transparent group-data-[variant=primary]/tabs-list:data-[active]:text-foreground',
        // line variant (deprecated alias for primary)
        'group-data-[variant=line]/tabs-list:rounded-none group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:pb-3 group-data-[variant=line]/tabs-list:data-[active]:bg-transparent group-data-[variant=line]/tabs-list:data-[active]:text-foreground',
        // folder variant — DS Folder tabs with filled active state
        'group-data-[variant=folder]/tabs-list:h-8 group-data-[variant=folder]/tabs-list:rounded-md group-data-[variant=folder]/tabs-list:px-3 group-data-[variant=folder]/tabs-list:data-[active]:bg-background group-data-[variant=folder]/tabs-list:data-[active]:text-foreground group-data-[variant=folder]/tabs-list:data-[active]:shadow-sm',
        // secondary (divider) variant
        'group-data-[variant=secondary]/tabs-list:rounded-none group-data-[variant=secondary]/tabs-list:bg-transparent group-data-[variant=secondary]/tabs-list:px-3 group-data-[variant=secondary]/tabs-list:first:pl-0 group-data-[variant=secondary]/tabs-list:last:pr-0 group-data-[variant=secondary]/tabs-list:data-[active]:text-foreground group-data-[variant=secondary]/tabs-list:data-[active]:font-semibold',
        // Active underline indicator — primary colour per DS (not neutral foreground)
        'after:absolute after:bg-primary after:opacity-0 after:transition-opacity group-data-[orientation=horizontal]/tabs:after:inset-x-0 group-data-[orientation=horizontal]/tabs:after:bottom-[-1px] group-data-[orientation=horizontal]/tabs:after:h-0.5 group-data-[orientation=vertical]/tabs:after:inset-y-0 group-data-[orientation=vertical]/tabs:after:-right-1 group-data-[orientation=vertical]/tabs:after:w-0.5',
        'group-data-[variant=default]/tabs-list:data-[active]:after:opacity-100',
        'group-data-[variant=primary]/tabs-list:data-[active]:after:opacity-100',
        'group-data-[variant=line]/tabs-list:data-[active]:after:opacity-100',
        className,
      )}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// TabsContent (wraps Base UI Tabs.Panel)
// ---------------------------------------------------------------------------
// We keep the export name as TabsContent to preserve the public API.
// ---------------------------------------------------------------------------

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Panel>) {
  return <TabsPrimitive.Panel data-slot="tabs-content" className={cn('flex-1 outline-none', className)} {...props} />
}

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants }
